import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Check, X, Loader2, Disc3, ExternalLink, ChevronLeft, ChevronRight,
  Inbox, CheckCircle2, XCircle, Pencil, Plus, Trash2
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";

const STATUS_COLORS = {
  pending:  { bg: "bg-amber-500/10",  text: "text-amber-400",  label: "Ausstehend" },
  approved: { bg: "bg-emerald-500/10", text: "text-emerald-400", label: "Angenommen" },
  rejected: { bg: "bg-red-500/10",    text: "text-red-400",    label: "Abgelehnt" },
};

const RELEASE_TYPES = ["Single", "EP", "Album", "Compilation", "Remix"];
const BUY_LINK_KEYS = [
  { key: "beatport", label: "Beatport" },
  { key: "spotify", label: "Spotify" },
  { key: "apple_music", label: "Apple Music" },
  { key: "amazon", label: "Amazon" },
];

// ─── Approve-Modal ────────────────────────────────────────────────────────────
function ApproveModal({ proposal, onConfirm, onClose, loading }) {
  const [form, setForm] = useState({
    interpret:        proposal.interpret || "",
    titel:            proposal.titel || "",
    label:            proposal.label || "",
    erscheinungsdatum: proposal.erscheinungsdatum || "",
    cover_url:        proposal.cover_url || "",
    typ:              proposal.typ || "Single",
    buy_links: {
      beatport:    proposal.buy_links?.beatport    || "",
      spotify:     proposal.buy_links?.spotify     || "",
      apple_music: proposal.buy_links?.apple_music || "",
      amazon:      proposal.buy_links?.amazon      || "",
    },
    tracklist_raw: "",   // Zeilengetrennte Tracklist
    categories_raw: "",  // Kommagetrennte Kategorien
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setBuy = (k, v) => setForm(p => ({ ...p, buy_links: { ...p.buy_links, [k]: v } }));

  const handleConfirm = () => {
    const tracklist = form.tracklist_raw
      .split("\n")
      .map(s => s.trim())
      .filter(Boolean);
    const categories = form.categories_raw
      .split(",")
      .map(s => s.trim())
      .filter(Boolean);
    onConfirm({
      interpret:        form.interpret.trim() || undefined,
      titel:            form.titel.trim() || undefined,
      label:            form.label.trim() || undefined,
      erscheinungsdatum: form.erscheinungsdatum || undefined,
      cover_url:        form.cover_url.trim() || undefined,
      typ:              form.typ || undefined,
      buy_links: Object.fromEntries(
        Object.entries(form.buy_links).filter(([, v]) => v.trim())
      ),
      tracklist,
      categories,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-zinc-950 border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] flex-shrink-0">
          <h3 className="text-white font-bold text-base flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-400" /> Release annehmen & ergänzen
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-zinc-500 hover:text-white transition">
            <X size={16} />
          </button>
        </div>

        {/* Body – scrollbar */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Quelle */}
          <div className="flex items-center gap-2 text-xs text-zinc-600 bg-zinc-900/50 rounded-xl px-3 py-2">
            <Disc3 size={12} className="text-rose-400" />
            Eingereicht von <span className="text-zinc-400 font-medium">{proposal.submitted_by_name}</span>
            {proposal.erscheinungsdatum && <span>· {proposal.erscheinungsdatum}</span>}
          </div>

          {/* Pflichtfelder */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Interpret *</label>
              <input value={form.interpret} onChange={e => set("interpret", e.target.value)}
                className="w-full bg-zinc-900 text-white rounded-xl px-3 py-2.5 text-sm outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition placeholder:text-zinc-700" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Titel *</label>
              <input value={form.titel} onChange={e => set("titel", e.target.value)}
                className="w-full bg-zinc-900 text-white rounded-xl px-3 py-2.5 text-sm outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition placeholder:text-zinc-700" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Label</label>
              <input value={form.label} onChange={e => set("label", e.target.value)}
                placeholder="Labelname"
                className="w-full bg-zinc-900 text-white rounded-xl px-3 py-2.5 text-sm outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition placeholder:text-zinc-700" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Typ</label>
              <select value={form.typ} onChange={e => set("typ", e.target.value)}
                className="w-full bg-zinc-900 text-white rounded-xl px-3 py-2.5 text-sm outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition">
                {RELEASE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Releasedatum</label>
              <input type="date" value={form.erscheinungsdatum} onChange={e => set("erscheinungsdatum", e.target.value)}
                className="w-full bg-zinc-900 text-white rounded-xl px-3 py-2.5 text-sm outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Cover URL</label>
              <input value={form.cover_url} onChange={e => set("cover_url", e.target.value)}
                placeholder="https://..."
                className="w-full bg-zinc-900 text-white rounded-xl px-3 py-2.5 text-sm outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition placeholder:text-zinc-700" />
            </div>
          </div>

          {/* Cover-Vorschau */}
          {form.cover_url && (
            <div className="flex items-center gap-3">
              <img src={form.cover_url} alt="Cover" className="w-14 h-14 rounded-xl object-cover ring-1 ring-white/10"
                onError={e => e.target.style.display = "none"} />
              <span className="text-zinc-600 text-xs">Cover-Vorschau</span>
            </div>
          )}

          {/* Buy Links */}
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">Kauflinks</label>
            <div className="space-y-2">
              {BUY_LINK_KEYS.map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-zinc-600 text-xs w-24 text-right flex-shrink-0">{label}</span>
                  <input value={form.buy_links[key]} onChange={e => setBuy(key, e.target.value)}
                    placeholder="https://..."
                    className="flex-1 bg-zinc-900 text-white rounded-xl px-3 py-2 text-xs outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition placeholder:text-zinc-700" />
                </div>
              ))}
            </div>
          </div>

          {/* Tracklist */}
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
              Tracklist <span className="text-zinc-700 font-normal normal-case">(eine Zeile = ein Track)</span>
            </label>
            <textarea
              value={form.tracklist_raw}
              onChange={e => set("tracklist_raw", e.target.value)}
              rows={4}
              placeholder={"01. Interpret – Titel\n02. Interpret – Titel\n..."}
              className="w-full bg-zinc-900 text-white rounded-xl px-3 py-2.5 text-sm outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition placeholder:text-zinc-700 resize-y font-mono"
            />
            {form.tracklist_raw.trim() && (
              <p className="text-zinc-700 text-xs mt-1">
                {form.tracklist_raw.split("\n").filter(s => s.trim()).length} Tracks erkannt
              </p>
            )}
          </div>

          {/* Kategorien */}
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
              Kategorien <span className="text-zinc-700 font-normal normal-case">(kommagetrennt)</span>
            </label>
            <input
              value={form.categories_raw}
              onChange={e => set("categories_raw", e.target.value)}
              placeholder="Hands Up, Makina, Hardstyle…"
              className="w-full bg-zinc-900 text-white rounded-xl px-3 py-2.5 text-sm outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition placeholder:text-zinc-700"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-6 py-4 border-t border-white/[0.06] flex-shrink-0">
          <button onClick={handleConfirm} disabled={loading || !form.interpret.trim() || !form.titel.trim()}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-sm transition">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {loading ? "Wird erstellt…" : "Release erstellen"}
          </button>
          <button onClick={onClose} disabled={loading}
            className="px-5 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white font-semibold text-sm transition">
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Haupt-Seite ──────────────────────────────────────────────────────────────
export default function ReleaseProposalsPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [approveModal, setApproveModal] = useState(null); // proposal object
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectLoading, setRejectLoading] = useState(false);
  const LIMIT = 20;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/release-proposals/admin?status=${statusFilter}&page=${page}&limit=${LIMIT}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); }, [statusFilter, page, token]); // eslint-disable-line

  const doApprove = async (overrides) => {
    const id = approveModal.id;
    setApproveLoading(true);
    try {
      const r = await api.post(`/release-proposals/admin/${id}/approve`, overrides, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(prev => prev.filter(i => i.id !== id));
      setTotal(t => t - 1);
      setApproveModal(null);
      // Kleiner Toast-Ersatz
      alert(`✅ Release erstellt: /release/${r.data.slug}`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Fehler");
    } finally {
      setApproveLoading(false);
    }
  };

  const doReject = async () => {
    const id = rejectModal;
    setRejectLoading(true);
    try {
      await api.post(`/release-proposals/admin/${id}/reject`, { reason: rejectReason }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(prev => prev.filter(i => i.id !== id));
      setTotal(t => t - 1);
      setRejectModal(null);
      setRejectReason("");
    } catch (e) {
      alert(e?.response?.data?.detail || "Fehler");
    } finally {
      setRejectLoading(false);
    }
  };

  const pages = Math.ceil(total / LIMIT);

  return (
    <AdminLayout>
      <Helmet><title>Release-Vorschläge | Admin</title></Helmet>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Disc3 size={18} className="text-rose-400" /> Release-Vorschläge
            </h1>
            <p className="text-zinc-500 text-sm mt-0.5">Von Artists eingereichte Release-Vorschläge</p>
          </div>
          {/* Status Filter */}
          <div className="flex gap-1 bg-zinc-900 rounded-xl p-1">
            {["pending", "approved", "rejected", "all"].map(s => (
              <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                data-testid={`filter-${s}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  statusFilter === s ? "bg-rose-600 text-white" : "text-zinc-500 hover:text-white"
                }`}>
                {s === "pending" ? "Ausstehend" : s === "approved" ? "Angenommen" : s === "rejected" ? "Abgelehnt" : "Alle"}
              </button>
            ))}
          </div>
        </div>

        {/* Liste */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin text-zinc-600" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <Inbox size={36} className="text-zinc-800 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Keine Vorschläge</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(item => {
              const sc = STATUS_COLORS[item.status] || STATUS_COLORS.pending;
              return (
                <div key={item.id} className="bg-zinc-900/60 border border-white/[0.06] rounded-2xl p-4 flex gap-4">
                  {/* Cover */}
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-zinc-800 flex-shrink-0">
                    {item.cover_url
                      ? <img src={item.cover_url} alt={item.titel} className="w-full h-full object-cover" onError={e => e.target.style.display = "none"} />
                      : <div className="w-full h-full flex items-center justify-center"><Disc3 size={18} className="text-zinc-700" /></div>
                    }
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-white font-bold text-sm leading-tight">{item.interpret}</p>
                        <p className="text-zinc-400 text-sm">{item.titel}</p>
                        <div className="flex items-center gap-3 mt-1 flex-wrap text-xs text-zinc-600">
                          {item.label && <span>{item.label}</span>}
                          {item.erscheinungsdatum && <span>{item.erscheinungsdatum}</span>}
                          {item.typ && <span className="px-1.5 py-0.5 rounded-md bg-zinc-800 text-zinc-500">{item.typ}</span>}
                          <span className="text-zinc-700">von {item.submitted_by_name}</span>
                        </div>
                        {/* Buy Links */}
                        {Object.keys(item.buy_links || {}).length > 0 && (
                          <div className="flex gap-2 mt-1.5 flex-wrap">
                            {Object.entries(item.buy_links).filter(([, v]) => v).map(([k, v]) => (
                              <a key={k} href={v} target="_blank" rel="noopener noreferrer"
                                className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 hover:text-white transition flex items-center gap-1 capitalize">
                                {k} <ExternalLink size={8} />
                              </a>
                            ))}
                          </div>
                        )}
                        {item.reject_reason && (
                          <p className="text-red-400/70 text-xs mt-1 italic">"{item.reject_reason}"</p>
                        )}
                        {item.release_id && (
                          <Link
                            to={`/admin/releases/${item.release_id}/edit`}
                            className="text-[10px] text-emerald-400/70 hover:text-emerald-400 transition flex items-center gap-1 mt-1"
                          >
                            Release bearbeiten <ExternalLink size={8} />
                          </Link>
                        )}
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${sc.bg} ${sc.text}`}>
                        {sc.label}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  {item.status === "pending" && (
                    <div className="flex flex-col gap-2 justify-center flex-shrink-0">
                      <button
                        onClick={() => setApproveModal(item)}
                        data-testid={`approve-btn-${item.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600/15 text-emerald-400 hover:bg-emerald-600/25 text-xs font-bold transition">
                        <Pencil size={11} /> Prüfen
                      </button>
                      <button
                        onClick={() => { setRejectModal(item.id); setRejectReason(""); }}
                        data-testid={`reject-btn-${item.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600/10 text-red-400 hover:bg-red-600/20 text-xs font-bold transition">
                        <XCircle size={11} /> Ablehnen
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-2 rounded-lg bg-zinc-900 text-zinc-400 hover:text-white disabled:opacity-40 transition">
              <ChevronLeft size={16} />
            </button>
            <span className="text-zinc-500 text-sm">{page} / {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
              className="p-2 rounded-lg bg-zinc-900 text-zinc-400 hover:text-white disabled:opacity-40 transition">
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Approve Modal */}
      {approveModal && (
        <ApproveModal
          proposal={approveModal}
          onConfirm={doApprove}
          onClose={() => setApproveModal(null)}
          loading={approveLoading}
        />
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-white font-bold mb-3">Ablehnen</h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Grund (optional)"
              rows={3}
              className="w-full bg-zinc-800 text-white rounded-xl px-3 py-2 text-sm outline-none resize-none mb-4 ring-1 ring-inset ring-white/[0.07]"
            />
            <div className="flex gap-2">
              <button onClick={doReject} disabled={rejectLoading}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-sm font-bold transition">
                {rejectLoading ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} />}
                Ablehnen
              </button>
              <button onClick={() => setRejectModal(null)} disabled={rejectLoading}
                className="flex-1 py-2 rounded-xl bg-zinc-800 text-zinc-400 hover:text-white text-sm font-bold transition">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
